import React from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';

export const AgentDashboardPage = () => {
  // Mock data - in real app, would fetch from API
  const agentName = 'Sarah Johnson';

  const stats = {
    unreadQueries: 12,
    totalQueries: 47,
    requestedManuscripts: 8,
    averageResponseTime: '5 days',
    acceptanceRate: 17,
  };

  const recentQueries = [
    {
      id: 1,
      author: 'Emma Rodriguez',
      title: 'The Silent Shore',
      genre: 'Literary Fiction',
      wordCount: 85000,
      receivedDate: '2024-02-05',
      status: 'unread',
      queryPreview: 'Dear Ms. Johnson, I am seeking representation for my literary fiction novel, THE SILENT SHORE, complete at 85,000 words...',
    },
    {
      id: 2,
      author: 'Michael Chen',
      title: 'Fractured Light',
      genre: 'Commercial Fiction',
      wordCount: 92000,
      receivedDate: '2024-02-04',
      status: 'unread',
      queryPreview: 'I hope this query finds you well. I\'m writing to you because of your interest in character-driven commercial fiction...',
    },
    {
      id: 3,
      author: 'Jennifer Blake',
      title: 'Where the Rivers Meet',
      genre: 'Women\'s Fiction',
      wordCount: 78000,
      receivedDate: '2024-02-03',
      status: 'read',
      queryPreview: 'Thank you for considering my query for WHERE THE RIVERS MEET, a women\'s fiction novel about family secrets and redemption...',
    },
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'query_received',
      message: 'New query from Emma Rodriguez for "The Silent Shore"',
      date: '2024-02-05',
      time: '2:30 PM',
    },
    {
      id: 2,
      type: 'manuscript_requested',
      message: 'Requested full manuscript from David Park for "Echoes of Tomorrow"',
      date: '2024-02-04',
      time: '4:15 PM',
    },
    {
      id: 3,
      type: 'response_sent',
      message: 'Sent pass response to Lisa Anderson for "City of Secrets"',
      date: '2024-02-03',
      time: '10:00 AM',
    },
    {
      id: 4,
      type: 'query_received',
      message: 'New query from Michael Chen for "Fractured Light"',
      date: '2024-02-04',
      time: '9:45 AM',
    },
  ];

  const upcomingDeadlines = [
    {
      id: 1,
      author: 'Rachel Martin',
      title: 'The Archive Keeper',
      type: 'Partial manuscript review',
      dueDate: '2024-02-10',
      daysRemaining: 5,
    },
    {
      id: 2,
      author: 'James Wilson',
      title: 'Beyond the Horizon',
      type: 'Full manuscript review',
      dueDate: '2024-02-15',
      daysRemaining: 10,
    },
  ];

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      query_received: '📧',
      manuscript_requested: '📄',
      response_sent: '✉️',
      offer_made: '🎉',
    };
    return icons[type] || '•';
  };

  const getStatusBadge = (status: string) => {
    if (status === 'unread') {
      return <Badge variant="warning">New</Badge>;
    }
    return null;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Welcome back, {agentName}
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Here's your query dashboard
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '24px',
          marginBottom: '40px',
        }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.warning,
              marginBottom: '8px',
            }}>
              {stats.unreadQueries}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Unread Queries
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '8px',
            }}>
              {stats.totalQueries}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Total Queries
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '8px',
            }}>
              {stats.requestedManuscripts}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Requested MSS
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '8px',
            }}>
              {stats.averageResponseTime}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Avg Response Time
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '8px',
            }}>
              {stats.acceptanceRate}%
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Request Rate
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card style={{
          backgroundColor: theme.colors.primaryLight,
          marginBottom: '40px',
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Quick Actions
          </h2>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <Button onClick={() => window.location.href = '/agent/queries'}>
              View Query Inbox ({stats.unreadQueries} unread)
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/agent/manuscripts'}>
              Requested Manuscripts
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/agent/profile/edit'}>
              Edit Profile & MSWL
            </Button>
          </div>
        </Card>

        {/* Recent Queries */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
            }}>
              Recent Queries
            </h2>
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/agent/queries'}
            >
              View All
            </Button>
          </div>

          {recentQueries.length === 0 ? (
            <Card>
              <p style={{
                textAlign: 'center',
                color: theme.colors.textSecondary,
                fontSize: '16px',
                padding: '40px 20px',
              }}>
                No recent queries
              </p>
            </Card>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {recentQueries.map((query) => (
                <Card key={query.id}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '24px',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                      }}>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.textPrimary,
                          margin: 0,
                        }}>
                          {query.title}
                        </h3>
                        {getStatusBadge(query.status)}
                      </div>
                      <p style={{
                        fontSize: '16px',
                        color: theme.colors.textSecondary,
                        marginBottom: '12px',
                      }}>
                        by {query.author}
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                        marginBottom: '12px',
                      }}>
                        <span>{query.genre}</span>
                        <span>•</span>
                        <span>{query.wordCount.toLocaleString()} words</span>
                        <span>•</span>
                        <span>Received: {query.receivedDate}</span>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        marginBottom: '16px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {query.queryPreview}
                      </p>
                      <Button
                        size="small"
                        onClick={() => window.location.href = `/agent/queries/${query.id}`}
                      >
                        Review Query
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Deadlines */}
        {upcomingDeadlines.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              marginBottom: '24px',
              color: theme.colors.textPrimary,
            }}>
              Upcoming Deadlines
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '16px',
            }}>
              {upcomingDeadlines.map((deadline) => (
                <Card key={deadline.id}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '500',
                      color: theme.colors.textPrimary,
                      margin: 0,
                      flex: 1,
                    }}>
                      {deadline.title}
                    </h3>
                    <Badge variant={deadline.daysRemaining <= 5 ? 'error' : 'warning'}>
                      {deadline.daysRemaining} days
                    </Badge>
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '8px',
                  }}>
                    by {deadline.author}
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    marginBottom: '12px',
                  }}>
                    {deadline.type} • Due: {deadline.dueDate}
                  </p>
                  <Button
                    size="small"
                    variant="outline"
                    onClick={() => window.location.href = '/agent/manuscripts'}
                  >
                    View Manuscript
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '400',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Recent Activity
          </h2>

          <Card>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {recentActivity.map((activity, index) => (
                <div
                  key={activity.id}
                  style={{
                    paddingBottom: index < recentActivity.length - 1 ? '16px' : '0',
                    borderBottom: index < recentActivity.length - 1
                      ? `1px solid ${theme.colors.border}`
                      : 'none',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}>
                    <span style={{
                      fontSize: '20px',
                      lineHeight: '1',
                      marginTop: '2px',
                    }}>
                      {getActivityIcon(activity.type)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '15px',
                        color: theme.colors.textPrimary,
                        marginBottom: '4px',
                      }}>
                        {activity.message}
                      </p>
                      <p style={{
                        fontSize: '13px',
                        color: theme.colors.textTertiary,
                      }}>
                        {activity.date} at {activity.time}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};
